import type { BillingUsageState, BillingUsageTotals } from "@lumora/core";
import { BillingUsageViewModel, formatUsd } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { useEffect, useMemo, useState } from "react";
import { platformClient } from "../../platformClient.js";
import { BillingUsageTable, currentUtcPeriod, isValidPeriod } from "./BillingUsageTable.js";
import { PlatformPageHeader } from "./PlatformPageHeader.js";

export interface BillingUsagePanelProps {
  state: BillingUsageState;
  totals: BillingUsageTotals;
  periodInput: string;
  onPeriodInput: (value: string) => void;
  onLoad: () => void;
  onDryRun: () => void;
}

/** Dumb view over BillingUsageViewModel state (#1243). */
export function BillingUsagePanel({
  state,
  totals,
  periodInput,
  onPeriodInput,
  onLoad,
  onDryRun,
}: BillingUsagePanelProps) {
  const valid = isValidPeriod(periodInput);
  const result = state.closeResult;
  return (
    <section className="ops-panel platform-billing" data-lumora-view="platform-billing">
      <div className="platform-billing-controls">
        <label>
          Period (UTC, YYYY-MM){" "}
          <input
            type="text"
            value={periodInput}
            placeholder="YYYY-MM"
            maxLength={7}
            data-lumora-billing-period
            onChange={(e) => onPeriodInput(e.target.value)}
          />
        </label>
        <button type="button" disabled={!valid || state.loading} onClick={onLoad}>
          {state.loading ? "Loading…" : "Load"}
        </button>
        <button
          type="button"
          data-lumora-billing-dryrun-btn
          disabled={!valid || state.closing}
          onClick={onDryRun}
        >
          {state.closing ? "Computing…" : "Dry-run close"}
        </button>
      </div>

      {state.error ? (
        <p className="portal-error" data-lumora-error>
          {state.error}
        </p>
      ) : null}

      <BillingUsageTable rows={state.rows} totals={totals} />

      {result ? (
        <div className="platform-billing-dryrun" data-lumora-billing-dryrun>
          <h3>Dry run {result.period}: nothing was created</h3>
          {result.rows.length === 0 ? (
            <p className="muted">No tenants would be billed.</p>
          ) : (
            <ul>
              {result.rows.map((r) => (
                <li key={r.tenantId}>
                  {r.slug}: API {formatUsd(r.apiCents)} + R2 {formatUsd(r.r2Cents)} + storage{" "}
                  {formatUsd(r.storageCents)}
                  {r.carriedCents > 0 ? ` + carried ${formatUsd(r.carriedCents)}` : ""} ={" "}
                  {formatUsd(r.billedCents)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

/** Platform console: per-tenant usage cost for a period + dry-run month close. */
export function PlatformBillingPage() {
  const vm = useMemo(() => new BillingUsageViewModel(platformClient), []);
  const state = useViewModel(vm);
  const [periodInput, setPeriodInput] = useState(() => currentUtcPeriod());

  useEffect(() => {
    void vm.load(periodInput);
    // initial load only; later loads are explicit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vm]);

  return (
    <>
      <PlatformPageHeader
        title="Billing"
        lead="Per-tenant metered usage and computed charges for a month."
      />
      <BillingUsagePanel
        state={state}
        totals={vm.totals()}
        periodInput={periodInput}
        onPeriodInput={setPeriodInput}
        onLoad={() => void vm.load(periodInput)}
        onDryRun={() => void vm.dryRunClose(periodInput)}
      />
    </>
  );
}
